import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TransferDetailBase } from '@/app/models/transferDetail';
import { useFinancialSubGroupQuery } from '@/app/services/financialGroupApi';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { useFinancialGroup } from '@/context/FinancialGroup';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Typography, useTheme } from '@mui/material';
import LaunchDetailsBox from './baseForms/info/LaunchDetailsBox';
import NewBookTransfer from './books/NewBookTransfer';
import NewConstructionTransfer from './construction/NewConstructionTransfer';
import NewConsumableTransfer from './consumable/NewConsumableTransfer';
import NewEquipmentTransfer from './equipment/NewEquipmentTransfer';
import NewEditRelatedServiceTransfer from './relatedService/NewEditRelatedServiceTransfer';
import NewEditThirdServicesTransfer from './thirdServices/NewEditThirdServicesTransfer';
import NewTrainingTransfer from './training/NewTrainingTransfer';
import NewTravelTransfer from './travel/NewTravelTransfer';

interface NewEditTransferCostProps {
  setDrawerOpen: (open: boolean) => void;
  selectedItem: TransferDetailBase | null;
}

export default function NewEditTransferCost(props: NewEditTransferCostProps) {
  const theme = useTheme();
  const { t } = useTranslation(['equipment', 'common']);

  const { setDrawerOpen, selectedItem } = props;
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState<number | ''>('');

  const [launchComplement] = useState<string>(selectedItem?.complement ? selectedItem.complement : '');

  const { selectedFinancialGroup } = useFinancialGroup();

  const { data: financialSubGroup } = useFinancialSubGroupQuery({ excludeRH: true });
  const selectedSubCategory = financialSubGroup?.find((c) => c.id === selectedSubCategoryId);

  const handleCancel = () => {
    setDrawerOpen(false);
  };

  const handleCategoryChange = (e: SelectChangeEvent<typeof selectedSubCategory>) => {
    const selectedId = e.target.value;
    setSelectedSubCategoryId(Number(selectedId));
  };

  const renderCategoryComponent = () => {
    switch (selectedSubCategory?.financialGroup.code) {
      case 'EQUIPMENTS':
        return (
          <NewEquipmentTransfer
            selectedItem={selectedItem as TransferDetailBase}
            setDrawerOpen={setDrawerOpen}
            selectedFinancialSubGroup={selectedSubCategory}
          />
        );
      case 'CONSTRUCTION':
        return (
          <NewConstructionTransfer
            selectedItem={selectedItem as TransferDetailBase}
            setDrawerOpen={setDrawerOpen}
            selectedFinancialSubGroup={selectedSubCategory}
          />
        );
      case 'BOOKS':
        return (
          <NewBookTransfer
            selectedItem={selectedItem as TransferDetailBase}
            setDrawerOpen={setDrawerOpen}
            selectedFinancialSubGroup={selectedSubCategory}
          />
        );
      case 'CONSUMABLES':
        return (
          <NewConsumableTransfer
            selectedItem={selectedItem as TransferDetailBase}
            setDrawerOpen={setDrawerOpen}
            selectedFinancialSubGroup={selectedSubCategory}
          />
        );
      case 'TRAVEL':
        return (
          <NewTravelTransfer
            selectedItem={selectedItem as TransferDetailBase}
            setDrawerOpen={setDrawerOpen}
            selectedFinancialSubGroup={selectedSubCategory}
          />
        );
      case 'TRAINING':
        return (
          <NewTrainingTransfer
            selectedItem={selectedItem as TransferDetailBase}
            setDrawerOpen={setDrawerOpen}
            selectedFinancialSubGroup={selectedSubCategory}
          />
        );
      case 'THIRDSERVICES':
        return (
          <NewEditThirdServicesTransfer
            selectedItem={selectedItem as TransferDetailBase}
            setDrawerOpen={setDrawerOpen}
            selectedFinancialSubGroup={selectedSubCategory}
          />
        );
      case 'RELATEDSERVICES':
        return (
          <NewEditRelatedServiceTransfer
            selectedItem={selectedItem as TransferDetailBase}
            setDrawerOpen={setDrawerOpen}
            selectedFinancialSubGroup={selectedSubCategory}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('transfer.cost')} />

        <LaunchDetailsBox
          totalValue={formatterMonetaryValue(selectedItem?.newDetail.value || null)}
          launchComplement={launchComplement}
          complementDisabled
        />

        <Box sx={{ width: '100%', height: theme.spacing(0.25), bgcolor: 'grey.300', mt: 2 }} />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
          <Typography component="p" variant="h1">
            {t('category')}
          </Typography>
          <FormControl sx={{ width: theme.spacing(30) }}>
            <Select
              displayEmpty
              value={selectedSubCategory || ''}
              onChange={(e) => {
                handleCategoryChange(e);
              }}
              renderValue={(selected) => {
                if (!selected) return <em>{t('select.category')}</em>;
                const category = financialSubGroup?.find((c) => c.id === selected.id);
                return t(category?.name || '');
              }}
              sx={{ height: 32, fontSize: 14 }}
            >
              <MenuItem value="">
                <em>{t('select.category')}</em>
              </MenuItem>
              {financialSubGroup &&
                financialSubGroup
                  .filter((item) => item.financialGroup.code !== selectedFinancialGroup?.code)
                  .map((item) => (
                    <MenuItem key={item.id} value={item.id} sx={{ fontSize: 13 }}>
                      {t(item.name)}
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ mt: 3 }}>{renderCategoryComponent()}</Box>
      </Box>
      {!selectedSubCategory && (
        <Box sx={{ mt: 'auto' }}>
          <DrawerButtons onCancel={handleCancel} onlySave={false} />
        </Box>
      )}
    </Box>
  );
}
